/* eslint-disable no-underscore-dangle */
/* eslint-disable max-lines */
import InventoryIcon from '@mui/icons-material/Inventory';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import TextField from 'common/forms/TextField';
import { filter, find, first, includes, isEmpty, map, some } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import DateField from 'common/forms/DateField';
import SelectField from 'common/forms/SelectField';
import TimeField from 'common/forms/TimeField';
import { validateTimeNotInFuture } from 'common/forms/formValidations';
import useRoles from 'common/hooks/useRoles';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { EnableShippingService } from 'common/utils';
import { format } from 'date-fns';
import { shipmentMode } from 'enums/common.enum';
import lineItemStatusEnum from 'enums/lineItem.enum';
import { useNavigate } from 'react-router-dom';
import { listShippingOptions, updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { verifyOrder } from 'store/thunks/patientThunks';
import { getFulfillingPharmacies } from 'store/thunks/pharmacyThunks';
import processingStateEnum from 'enums/processingState.enum';
import PatientPrescriptionDialog from '../PrescriptionsTab/PatientPrescriptionDialog';
import AddressValidationButton from './AddressValidation';
import ReviewOrderVerificationButton from './ReviewOrderVerificationButton';

const ShipmentModeWatcher = ({ form, shippingOptions, currentMode }) => {
  const savedOptionsRef = useRef({});
  const prevModeRef = useRef(currentMode);

  useEffect(() => {
    const prevMode = prevModeRef.current;
    if (prevMode === currentMode) return;

    const currentOption = form.getState().values.shipmentType;
    savedOptionsRef.current[prevMode] = currentOption;
    prevModeRef.current = currentMode;

    if (savedOptionsRef.current[currentMode]) {
      const availableOptions =
        currentMode === shipmentMode.PRX_SHIPMENT
          ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
          : shippingOptions;
      const saved = savedOptionsRef.current[currentMode];
      const isSavedValid = availableOptions.some((opt) => opt.optionCode === saved);
      if (isSavedValid) {
        form.change('shipmentType', saved);
        return;
      }
    }

    const availableOptions =
      currentMode === shipmentMode.PRX_SHIPMENT
        ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
        : shippingOptions;

    const currentOption2 = form.getState().values.shipmentType;
    const isCurrentValid = availableOptions.some((opt) => opt.optionCode === currentOption2);
    if (!isCurrentValid && availableOptions.length > 0) {
      const defaultOption = find(availableOptions, { isDefault: true }) || availableOptions[0];
      form.change('shipmentType', defaultOption.optionCode);
    }
  }, [currentMode, form, shippingOptions]);

  return null;
};

ShipmentModeWatcher.propTypes = {
  form: PropTypes.shape({
    getState: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
  }).isRequired,
  shippingOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentMode: PropTypes.string.isRequired,
};

const VerifyOrderButton = ({ mpi, order, isRxEntryOrder, isManualOrder, onSuccess, disabled }) => {
  const hasAccess = useRoles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [toolTipMsg, setToolTipMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [isManualConfirmationNeeded, setIsManualConfirmationNeeded] = useState(false);
  const [formData, setFormData] = useState({});
  const [skipValidation, setSkipValidation] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);

  const { shippingOptions } = useSelector(({ systemConfigs }) => systemConfigs);
  const { currentTenantId } = useSelector(({ auth }) => auth);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId] ?? []);
  const defaultShipmentMode =
    find(fulfillingPharmacies, { fulfillingPharmacyId: order.fulfillingPharmacyId })?.shipmentConfig
      ?.shipmentRoutes?.routeTo ?? shipmentMode.SHIP_VIA_SHIPSTATION;

  const { lineItems } = order;
  const isRefillOrder = order.fillType === 'REFILL';

  const batchShipmentExcludedStatuses = [lineItemStatusEnum.SPLIT, lineItemStatusEnum.CANCELED];
  const batchShipmentExcludedProcessingStatuses = [
    processingStateEnum.IN_SHIPMENT,
    processingStateEnum.SHIPPED,
    processingStateEnum.CANCELED,
    processingStateEnum.SPLIT,
  ];
  const lineItemsInBatchShipment = filter(
    lineItems,
    (item) =>
      item.inBatchShipmentQueue === true &&
      !batchShipmentExcludedStatuses.includes(item.lineItemStatus) &&
      !batchShipmentExcludedProcessingStatuses.includes(item.processingState)
  );

  const lineItemsNotInBatchShipment = filter(lineItems, {
    inBatchShipmentQueue: false,
    lineItemStatus: lineItemStatusEnum.PRODUCT_FULFILLED,
  });

  const lineItemsNotInBatchShipmentIds = map(lineItemsNotInBatchShipment, 'lineItemId');

  const defaultShippingOption = first(lineItemsNotInBatchShipment)?.shippingOption;

  const isDisabled = !lineItemsNotInBatchShipment.length;

  const listObject = [
    {
      heading: 'In Batch Shipment',
      value: !isEmpty(lineItemsInBatchShipment) && lineItemsInBatchShipment,
    },
    {
      heading: 'In Order Shipment',
      value: !isEmpty(lineItemsNotInBatchShipment) && lineItemsNotInBatchShipment,
    },
  ];

  const isManualShipment = !isEmpty(lineItemsNotInBatchShipment);

  const activeLineItems = filter(
    order?.lineItems,
    ({ lineItemStatus }) =>
      !includes([lineItemStatusEnum.CANCELED, lineItemStatusEnum.SPLIT], lineItemStatus)
  );

  const unfulfilledLineItems = useMemo(() => {
    if (order.status !== 'ORDER_RE_VERIFICATION') return [];

    return order.lineItems.filter(
      (item) => item.lineItemStatus === lineItemStatusEnum.PRODUCT_FULFILLMENT
    );
  }, [order.status, order.lineItems]);

  const disableVerifyOrderButton =
    !isEmpty(unfulfilledLineItems) ||
    some(map(activeLineItems, 'taggedForReview')) ||
    order.isProcessedExternal;

  useEffect(() => {
    if (order.isProcessedExternal) {
      setToolTipMsg('Order processing is external');
    } else if (!isEmpty(unfulfilledLineItems)) {
      setToolTipMsg('Line item needs to be filled');
    } else if (disableVerifyOrderButton) {
      setToolTipMsg('Line Items needs to be reviewed');
    }
  }, [order.isProcessedExternal, disableVerifyOrderButton, unfulfilledLineItems]);

  const openVerifyDialog = () => setShowVerifyDialog(true);
  const closeVerifyDialog = () => {
    setShowVerifyDialog(false);
  };

  const closePrescriptionDialog = () => setShowPrescriptionDialog(false);

  const handlePrescriptionForm = (prescriptionId) => {
    setSelectedPrescriptionId(prescriptionId);
    setShowPrescriptionDialog(!showPrescriptionDialog);
  };

  const handleVerifyOrder = (data) => {
    const isAutoShipEnabled = !isEmpty(lineItemsNotInBatchShipmentIds);

    if (isEmpty(formData)) {
      setFormData(data);
    }

    const shipDateStr = data?.shipDate ? format(new Date(data.shipDate), 'yyyy-MM-dd') : null;
    const shipTimeStr = data?.shipTime || null;

    const finalShipmentMode = data.shipmentMode;

    setIsLoading(true);
    return dispatch(
      verifyOrder({
        mpi,
        isAutoShipEnabled,
        orderId: order.orderId,
        lineItemIds: lineItemsNotInBatchShipmentIds,
        ...(isManualShipment && { shipmentMode: data.shipmentMode }),
        ...(skipValidation && { skipAddressValidation: skipValidation }),
        ...(isAutoShipEnabled &&
          (finalShipmentMode === shipmentMode.PRX_SHIPMENT ||
            finalShipmentMode === shipmentMode.SHIP_VIA_SHIPSTATION) && {
            shipmentType: data.shipmentType,
          }),
        ...(finalShipmentMode === shipmentMode.EXTERNAL_SHIPMENT && {
          shipmentType: 'EXTERNAL',
          externalOrderId: data?.externalShipmentId ? data.externalShipmentId : uuidV4(),
          trackingNumber: data.trackingNumber,
          carrierCode: data.carrierCode,
          shipDate: shipDateStr,
          shipTime: shipTimeStr,
        }),
      })
    )
      .then((response) => {
        setSkipValidation(false);
        if (response?.__typename === 'Addresses') {
          setIsLoading(false);
          setShowAddressDialog(true);
          setSuggestedAddresses(response?.addresses);
          setIsManualConfirmationNeeded(true);
        } else if (response?.__typename === 'PatientOrder') {
          closeVerifyDialog();
          enqueueSnackbar(`Order ${response.orderId} Verified`, { variant: 'success' });
          Promise.resolve(onSuccess?.()).finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        const errorCode = err?.response?.errors?.[0]?.extensions?.code;
        if (errorCode === 'SHIPPING_ADDRESS_NOT_VALID') {
          closeVerifyDialog();
          setShowAddressDialog(true);
          setIsManualConfirmationNeeded(true);
        }
      });
  };

  const handleAddressVerification = () => {
    const isAutoShipEnabled = !isEmpty(lineItemsNotInBatchShipmentIds);

    setIsLoading(true);
    return dispatch(
      verifyOrder({
        mpi,
        isAutoShipEnabled,
        orderId: order.orderId,
        ...(isManualShipment && { shipmentMode: formData.shipmentMode }),
        ...(isAutoShipEnabled && {
          shipmentType: formData.shipmentType,
          lineItemIds: lineItemsNotInBatchShipmentIds,
        }),
        skipAddressValidation: true,
      })
    )
      .then((response) => {
        setShowAddressDialog(false);
        if (response?.__typename === 'PatientOrder') {
          closeVerifyDialog();
          enqueueSnackbar(`Order ${response.orderId} Verified`, { variant: 'success' });
          Promise.resolve(onSuccess?.()).finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  useEffect(() => {
    if (isEmpty(shippingOptions)) {
      dispatch(listShippingOptions());
    }
  }, [shippingOptions, dispatch]);

  useEffect(() => {
    if (isEmpty(fulfillingPharmacies)) {
      dispatch(getFulfillingPharmacies());
    }
  }, [fulfillingPharmacies, dispatch]);

  return (
    <>
      {showPrescriptionDialog && (
        <PatientPrescriptionDialog
          contextId={selectedPrescriptionId}
          closeDialog={closePrescriptionDialog}
          mpi={mpi}
          open={showPrescriptionDialog}
        />
      )}

      {showAddressDialog && (
        <AddressValidationButton
          open={showAddressDialog}
          onClose={() => {
            setShowAddressDialog(false);
            setIsManualConfirmationNeeded(false);
            openVerifyDialog();
          }}
          patientId={mpi}
          order={order}
          onEmitData={handleAddressVerification}
          suggestedAddresses={suggestedAddresses}
          requireManualConfirmation={isManualConfirmationNeeded}
        />
      )}

      <Dialog open={showVerifyDialog}>
        <DialogTitle sx={{ px: 3 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6' component='span' sx={{ py: 2 }}>
              Verify Order
            </Typography>
            {isRxEntryOrder && hasAccess.markOrderForExternalProcessing && (
              <ReviewOrderVerificationButton
                mpi={mpi}
                disabled={isManualOrder}
                isRefillOrder={isRefillOrder}
                orderId={order.orderId}
                isManualOrder={isManualOrder}
              />
            )}
          </Box>
        </DialogTitle>

        <Form
          onSubmit={handleVerifyOrder}
          initialValues={{
            shipmentType: defaultShippingOption,
            carrierCode: 'UPS',
            shipmentMode: defaultShipmentMode,
          }}
          render={({ handleSubmit, values, form }) => {
            const availableShippingOptions =
              values.shipmentMode === shipmentMode.PRX_SHIPMENT
                ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
                : shippingOptions;

            const isShipmentTypeValid =
              values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT ||
              availableShippingOptions.some((opt) => opt.optionCode === values.shipmentType);

            const isSubmitDisabled =
              !hasAccess.verifyOrder ||
              (values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT && !values.trackingNumber) ||
              (isManualShipment && !isShipmentTypeValid);

            return (
              <form noValidate onSubmit={handleSubmit}>
                <ShipmentModeWatcher
                  form={form}
                  shippingOptions={shippingOptions}
                  currentMode={values.shipmentMode}
                />
                <DialogContent sx={{ minWidth: 500, py: 0, my: 0 }}>
                  <Typography variant='h6' fontWeight='bold' sx={{ mb: 1 }}>
                    Shipment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <List
                        sx={{
                          width: '100%',
                          position: 'relative',
                          overflow: 'auto',
                          maxHeight: 300,
                          '& ul': { padding: 0 },
                        }}
                        subheader={<li />}
                      >
                        {map(listObject, (listItem) => (
                          <li key={`section-${listItem.heading}`}>
                            <ul>
                              <ListSubheader sx={{ fontWeight: 'bold' }}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                  {listItem.heading}
                                </Typography>
                              </ListSubheader>

                              {!listItem.value && (
                                <ListItem key={`item-${listItem.heading}`} sx={{ p: 2, pl: '13%' }}>
                                  <Typography>Nothing Found...</Typography>
                                </ListItem>
                              )}

                              {map(listItem.value, (lineItem) => (
                                <li key={`section-${lineItem.lineItemId}`}>
                                  <ul>
                                    <ListItem key={`item-${lineItem.rxNumber}`}>
                                      <ListItemIcon sx={{ alignItems: 'center', gap: 1 }}>
                                        {lineItem.inBatchShipmentQueue && (
                                          <Tooltip
                                            title={batchShippingTooltip({
                                              isBusinessAddress: lineItem.isBusinessAddress,
                                              isClickable: true,
                                            })}
                                            arrow
                                            placement='top'
                                          >
                                            <BatchShippingIcon
                                              onClick={() =>
                                                navigateToBatchShipmentQueue(order.orderId)
                                              }
                                              color={perfectRxTheme.palette.warning.warningColour}
                                              showBadge={lineItem.isBusinessAddress}
                                            />
                                          </Tooltip>
                                        )}

                                        {!lineItem.inBatchShipmentQueue &&
                                          lineItem.isBusinessAddress && (
                                            <BusinessAddressIndicator />
                                          )}

                                        {!lineItem.inBatchShipmentQueue && !lineItem.default && (
                                          <InventoryIcon color='primary' />
                                        )}
                                      </ListItemIcon>

                                      <ListItemText
                                        primary={lineItem?.itemName ?? lineItem.default}
                                        secondary={lineItem?.rxNumber}
                                      />

                                      <Button
                                        size='small'
                                        variant='outlined'
                                        onClick={() =>
                                          handlePrescriptionForm(lineItem?.prescriptionId)
                                        }
                                      >
                                        View
                                      </Button>
                                    </ListItem>
                                  </ul>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </List>

                      <Typography fontWeight='bold'>
                        {isDisabled
                          ? ' Above all line items are in the batch shipment queue. Complete the batch shipment or remove items from the batch queue to ship separately'
                          : 'The items under Order Shipment will be sent in this shipment. Click Confirm to proceed'}
                      </Typography>
                    </Grid>

                    {isManualShipment && (
                      <>
                        <Grid item xs={12}>
                          <SelectField
                            id='VerifyOrder-shippingService-dropdown'
                            label='Shipping Service'
                            name='shipmentMode'
                            disabled={isDisabled}
                            lexicographicSort={false}
                          >
                            <MenuItem value={shipmentMode.SHIP_VIA_SHIPSTATION}>
                              Ship Via ShipStation
                            </MenuItem>
                            {EnableShippingService && (
                              <MenuItem value={shipmentMode.PRX_SHIPMENT}>
                                Send to Shipping
                              </MenuItem>
                            )}
                            <MenuItem value={shipmentMode.EXTERNAL_SHIPMENT}>
                              Enter Shipment Manually
                            </MenuItem>
                          </SelectField>
                        </Grid>
                        {values.shipmentMode !== shipmentMode.EXTERNAL_SHIPMENT && (
                          <Grid item xs={12}>
                            <SelectField
                              id='SendToShipping-shippingType-dropdown'
                              label='Select Shipping Method'
                              name='shipmentType'
                              disabled={isDisabled}
                            >
                              {map(
                                values.shipmentMode === shipmentMode.PRX_SHIPMENT
                                  ? filter(shippingOptions, (option) =>
                                      option.carrierCode.toLowerCase().includes('fedex')
                                    )
                                  : shippingOptions,
                                ({ shippingOption, optionCode }, key) => {
                                  return (
                                    <MenuItem value={optionCode} key={key}>
                                      {shippingOption}
                                    </MenuItem>
                                  );
                                }
                              )}
                            </SelectField>
                          </Grid>
                        )}
                      </>
                    )}

                    {values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT && (
                      <Grid item xs={12}>
                        <SelectField
                          id='Shipment-Manually-dropdown'
                          label='Carrier Code'
                          name='carrierCode'
                          value='UPS'
                          disabled
                        >
                          <MenuItem value='UPS'>UPS</MenuItem>
                        </SelectField>

                        <TextField label='Tracking Number' name='trackingNumber' required />

                        <DateField
                          label='Shipping Date'
                          name='shipDate'
                          id='BatchShipmentsTable-shipDate-dateField'
                          required
                          maxDate={new Date()}
                        />

                        <TimeField
                          label='Shipping Time (24-hour)'
                          name='shipTime'
                          id='BatchShipmentsTable-shipTime-timeField'
                          required
                          helperText='Use 24-hour format, e.g., 14:30'
                          validations={[
                            validateTimeNotInFuture('Time cannot be in the future', 'shipDate'),
                          ]}
                        />

                        <TextField
                          label='External Shipment ID (If Available)'
                          name='externalShipmentId'
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Box
                    display='flex'
                    alignItems='center'
                    sx={{
                      p: 2,
                      backgroundColor: (theme) => theme.palette.grey[100],
                      borderRadius: 1,
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    <Typography
                      fontWeight={600}
                      variant='body1'
                      sx={{
                        color:
                          values.shipmentMode === shipmentMode.SHIP_VIA_SHIPSTATION
                            ? 'error.main'
                            : 'inherit',
                      }}
                    >
                      I acknowledge that order {order.orderId} has been verified
                      {isRxEntryOrder && ' and was placed through RX ENTRY.'}
                      {values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT
                        ? ' Shipment details have been manually entered and will not be processed via Fedex or ShipStation'
                        : ` Shipment will be processed via ${values.shipmentMode === shipmentMode.SHIP_VIA_SHIPSTATION ? 'ShipStation' : 'shipment service'}.`}
                    </Typography>
                  </Box>
                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={closeVerifyDialog}
                    color='secondary'
                    size='medium'
                    variant='outlined'
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={isLoading}
                    disabled={isSubmitDisabled}
                  >
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>

      <Tooltip title={toolTipMsg} placement='top' arrow>
        <span>
          <LoadingButton
            variant='contained'
            onClick={openVerifyDialog}
            loading={isLoading}
            disabled={disabled || disableVerifyOrderButton}
            sx={{
              px: 1,
              fontSize: '0.75rem',
              minHeight: '30px',
              lineHeight: 'normal',
            }}
          >
            Verify Order
          </LoadingButton>
        </span>
      </Tooltip>
    </>
  );
};

export default VerifyOrderButton;

VerifyOrderButton.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    status: PropTypes.string,
    orderType: PropTypes.string,
    orderCost: PropTypes.string,
    isProcessedExternal: PropTypes.bool,
    fulfillingPharmacyId: PropTypes.string,
    partner: PropTypes.shape({ partnerName: PropTypes.string }),
    fillType: PropTypes.string,
    shippingAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        itemName: PropTypes.string,
        needsByDate: PropTypes.string,
        itemType: PropTypes.string,
        lineItemStatus: PropTypes.string.isRequired,
        cost: PropTypes.number,
        lineItemId: PropTypes.string.isRequired,
        prescriptionId: PropTypes.string.isRequired,
      })
    ),
    partnerAccountId: PropTypes.string,
  }).isRequired,
  isRxEntryOrder: PropTypes.bool.isRequired,
  mpi: PropTypes.string.isRequired,
  isManualOrder: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
  disabled: PropTypes.bool,
};

VerifyOrderButton.defaultProps = {
  onSuccess: undefined,
  disabled: false,
};
